/**
 * OYUNCU SEÇİMLERİ (1/N ve N/M)
 *
 * Bu modül SAF'tır: girdi alır, seçimlerin birleşik etkilerini döndürür.
 * Yan etkisi yoktur, dosya sistemi/veritabanı bilmez.
 *
 * TASARIM İLKESİ — "Sessiz varsayılan yok":
 * Bir seçim grubu için gereken sayıda seçim yapılmadıysa, o grubun
 * HİÇBİR etkisi uygulanmaz ve eksiklik `warnings` içinde bildirilir.
 * 5e'de örneğin Ejder Soylu'nun nefes hasar türü seçilmeden direnci
 * hesaplanamaz; motor "fire" varsayılanı uydurmaz çünkü bu oyuncunun
 * karakteridir, bot'un değil.
 */

import type {
  AbilityKey,
  ChoiceEffects,
  ChoiceGroup,
  InnateSpellGrant,
  ResolvedChoice,
} from './types.js'

/** Bir gruptaki seçimlerin birleşik etkisi. */
export interface MergedEffects {
  abilityBonuses: Partial<Record<AbilityKey, number>>
  skillProficiencies: string[]
  toolProficiencies: string[]
  languageProficiencies: string[]
  savingThrowProficiencies: AbilityKey[]
  grantsProficiencies: string[]
  damageResistances: string[]
  damageImmunities: string[]
  damageVulnerabilities: string[]
  conditionImmunities: string[]
  acBonus: number
  acBase: number | null
  defenseMode: 'unarmoredDex' | 'unarmoredDexCon' | null
  speedBonus: number
  size: string | null
  reachBonus: number
  darkvision: number | null
  cantrips: string[]
  innateSpells: InnateSpellGrant[]
  feats: string[]
  naturalWeapons: NonNullable<ChoiceEffects['naturalWeapon']>[]
  damageTypes: string[]
  spellSlotsOverride: Record<string, number>
}

function emptyEffects(): MergedEffects {
  return {
    abilityBonuses: {},
    skillProficiencies: [],
    toolProficiencies: [],
    languageProficiencies: [],
    savingThrowProficiencies: [],
    grantsProficiencies: [],
    damageResistances: [],
    damageImmunities: [],
    damageVulnerabilities: [],
    conditionImmunities: [],
    acBonus: 0,
    acBase: null,
    defenseMode: null,
    speedBonus: 0,
    size: null,
    reachBonus: 0,
    darkvision: null,
    cantrips: [],
    innateSpells: [],
    feats: [],
    naturalWeapons: [],
    damageTypes: [],
    spellSlotsOverride: {},
  }
}

function pushUnique(target: string[], values: readonly string[] | undefined): void {
  for (const v of values ?? []) if (!target.includes(v)) target.push(v)
}

/** Tek bir `ChoiceEffects` nesnesini birikime ekler. */
export function mergeEffects(target: MergedEffects, effects: ChoiceEffects): void {
  for (const [key, value] of Object.entries(effects.abilityBonuses ?? {})) {
    const ability = key as AbilityKey
    target.abilityBonuses[ability] = (target.abilityBonuses[ability] ?? 0) + (value as number)
  }
  pushUnique(target.skillProficiencies, effects.skillProficiencies)
  pushUnique(target.toolProficiencies, effects.toolProficiencies)
  pushUnique(target.languageProficiencies, effects.languageProficiencies)
  pushUnique(
    target.savingThrowProficiencies as unknown as string[],
    effects.savingThrowProficiencies,
  )
  pushUnique(target.grantsProficiencies, effects.grantsProficiencies)
  pushUnique(target.damageResistances, effects.damageResistances)
  pushUnique(target.damageImmunities, effects.damageImmunities)
  pushUnique(target.damageVulnerabilities, effects.damageVulnerabilities)
  pushUnique(target.conditionImmunities, effects.conditionImmunities)
  pushUnique(target.cantrips, effects.cantrips)
  pushUnique(target.feats, effects.feats)
  pushUnique(target.damageTypes, effects.damageType ? [effects.damageType] : [])

  target.acBonus += effects.acBonus ?? 0
  target.speedBonus += effects.speedBonus ?? 0
  target.reachBonus += effects.reachBonus ?? 0
  if (effects.acBase != null) target.acBase = effects.acBase
  if (effects.defenseMode) target.defenseMode = effects.defenseMode
  if (effects.size) target.size = effects.size
  if (effects.darkvision != null) target.darkvision = effects.darkvision
  if (effects.naturalWeapon) target.naturalWeapons.push(effects.naturalWeapon)
  if (effects.innateSpells) target.innateSpells.push(...effects.innateSpells)
  for (const [slotLevel, count] of Object.entries(effects.spellSlotsOverride ?? {})) {
    target.spellSlotsOverride[slotLevel] = Math.max(
      target.spellSlotsOverride[slotLevel] ?? 0,
      count,
    )
  }
}

export interface ChoiceResolution {
  /** Uygulanabilir (seviye acilmış) gruplar. */
  active: ChoiceGroup[]
  /** Hicbiri uygulanmayan tum gruplar — arayuz listeleyebilsin diye. */
  all: ChoiceGroup[]
  /** Uygulanabilir gruplarda yapilan tum secimlerin birlestirilmis etkisi. */
  merged: MergedEffects
  /** Arayuzun dolduracagi cozumlenmis durum. */
  resolved: ResolvedChoice[]
  /** Eksik veya hatali secimler icin uyari metinleri. */
  warnings: string[]
}

/**
 * Karakterin secimlerini cozer ve birlestirilmis etkiyi dondurur.
 *
 * @param groups Irkin tanimladigi tum secim gruplari
 * @param picked `CharacterInput.choices` — grup id -> secenek id listesi
 * @param level  Karakterin seviyesi (seviye kilitli gruplar icin)
 */
export function resolveChoices(
  groups: readonly ChoiceGroup[],
  picked: Record<string, string[]> | undefined,
  level: number,
): ChoiceResolution {
  const out: ChoiceResolution = {
    active: [],
    all: [...groups],
    merged: emptyEffects(),
    resolved: [],
    warnings: [],
  }

  for (const group of groups) {
    const required = group.min
    const availableAt = group.grantedAtLevel ?? 1
    const unlocked = level >= availableAt
    const isPending = group.pending === true

    // ⚠️ PENDING GRUPLAR
    // Seçenek listesi henüz yazılmamış. Bu bir İÇERİK eksiğidir, oyuncu
    // hatası değildir — bu yüzden "şu seçimi yapmadın" uyarısı ÜRETİLMEZ.
    // Grup yapı olarak hazırdır: DM sonradan sadece `options` dizisini doldurur.
    if (isPending) {
      out.resolved.push({
        groupId: group.id,
        groupLabel: group.label,
        groupLabelTr: group.labelTr,
        selected: [],
        required,
        max: group.max,
        chosen: 0,
        complete: true,
        unlocked,
        pending: true,
        pendingCount: group.pendingCount ?? group.max,
        available: [],
      })
      continue
    }

    if (!unlocked) {
      out.resolved.push({
        groupId: group.id,
        groupLabel: group.label,
        groupLabelTr: group.labelTr,
        selected: [],
        required,
        max: group.max,
        chosen: 0,
        complete: true, // henuz acilmadi, eksik sayilmaz
        unlocked: false,
        pending: false,
        pendingCount: 0,
        available: group.options.map((o) => ({ id: o.id, nameTr: o.nameTr })),
      })
      continue
    }

    out.active.push(group)

    const chosenIds = (picked?.[group.id] ?? []).filter((id) =>
      group.options.some((o) => o.id === id),
    )
    const unknown = (picked?.[group.id] ?? []).filter(
      (id) => !group.options.some((o) => o.id === id),
    )

    for (const id of unknown) {
      out.warnings.push(
        `"${group.labelTr}" seciminde gecersiz secenek: "${id}". Bu etki UYGULANMADI.`,
      )
    }

    // Coklu secimde ayni secenek iki kez sayilmasin
    const unique = [...new Set(chosenIds)]
    if (unique.length !== chosenIds.length) {
      out.warnings.push(
        `"${group.labelTr}" icinde ayni secenek birden fazla kez listelenmis; tekillestirildi.`,
      )
    }

    if (unique.length > group.max) {
      out.warnings.push(
        `"${group.labelTr}" icin en fazla ${group.max} secim yapilabilir; ${unique.length} secildi. Fazlasi UYGULANMADI.`,
      )
    }

    if (unique.length < required) {
      out.warnings.push(
        `"${group.labelTr}" icin ${required} secim yapilmali, ${unique.length} yapildi. Eksik secimin etkileri UYGULANMADI.`,
      )
    }

    // Yeterli secim yapildiysa etkileri uygula
    const applied = unique.slice(0, group.max)
    if (unique.length >= required) {
      for (const id of applied) {
        const option = group.options.find((o) => o.id === id)
        if (option) mergeEffects(out.merged, option.effects)
      }
    }

    out.resolved.push({
      groupId: group.id,
      groupLabel: group.label,
      groupLabelTr: group.labelTr,
      selected: applied
        .map((id) => group.options.find((o) => o.id === id))
        .filter((o): o is NonNullable<typeof o> => Boolean(o))
        .map((o) => ({ id: o.id, nameTr: o.nameTr })),
      required,
      max: group.max,
      chosen: applied.length,
      complete: unique.length >= required && unique.length <= group.max,
      unlocked: true,
      pending: false,
      pendingCount: 0,
      available: group.options.map((o) => ({ id: o.id, nameTr: o.nameTr })),
    })
  }

  return out
}
